/**
 * Screens that lock an org out of the app (Ersteinrichtung wizard + waiting
 * page, seat overrun). They render outside the app chrome — the $slug layout
 * skips AppLayout for them and the boot skeleton must not flash a sidebar.
 *
 * The single declaration: `resolveLockRedirect` returns one of these and the
 * segments are derived, so a fourth lock cannot half-land as a route nobody
 * exempts from the chrome.
 */
export const LOCK_ROUTES = [
  '/$slug/initial-setup',
  '/$slug/setup-pending',
  '/$slug/seat-overrun'
] as const;

export type LockRoute = (typeof LOCK_ROUTES)[number];

export const LOCK_ROUTE_SEGMENTS = LOCK_ROUTES.map((route) => lockRouteSegment(route));

/** The `/$slug`-less tail of a lock route, the shape a pathname can contain. */
export function lockRouteSegment(route: LockRoute): string {
  return route.slice('/$slug'.length);
}

export const ROUTES = {
  DASHBOARD: 'dashboard',
  PARTNER: 'partner',
  ACTIVE_MONITORING: 'active-monitoring',
  CUSTOMER_ORGANIZATION: 'customer-organization',
  SOFTWARE_LIST: 'software-list',
  ALL_ORGANIZATION: 'organizations/all',
  GLOBAL_SOFTWARE_LIST: 'global-software',
  EMAIL_TEMPLATES: 'email-templates',
  TRANSLATIONS: 'translations',
  TRANSLATIONS_LIST: 'list',
  PROCESSES: 'processes',
  ALL_PROCESSES: 'all',
  CLIENT_MANAGEMENT: 'client-management',
  CLIENT_DETAIL: 'client-management/:clientId',
  PARTNER_SUPPORT: 'partner-support',
  PARTNER_RELATIONSHIP: 'partner-relationship',
  DEPARTMENT_MEMBERS: 'my-dep-members',
  DEPARTMENT_ABSENCES: 'absences',
  ALL_DEPARTMENTS: 'all',
  ALL_MEMBERS: 'members/all',
  EXPERT_CALIBRATION: 'expert-calibration',
  MEMBER_EDIT: 'members/edit',
  ALL_SHARED_INBOXES: 'shared-inboxes/all',
  ALL_SOFTWARE_SYSTEMS: 'software-systems/all',
  SHARES_INBOXES: 'my-shares-inboxes',
  EXTERNAL_CONTACTS: 'external-contacts',
  ANALYTICS: 'analytics',
  ORGANIZATION: 'organization',
  ALL_ARTIFACTS: 'artifacts/all',
  ORG_CHART: 'chart',
  SURVEY: 'survey',
  SUPPORT: 'support',
  ORGANIZATION_SETTINGS: 'organization-settings',
  USER_MANAGEMENT: 'user-management',
  OVERVIEW: 'overview',
  CLIENT_LICENSE: 'client-license',
  DASHBOARD_OVERVIEW: 'overview',
  SALES_POTENTIAL: 'sales-potential',
  COMMISSION: 'commission',
  DATA: 'data',
  NOTIFICATION: 'notifications',
  PROFILE: 'profiles',
  USER_PROFILE: 'profile',
  USER_ACCOUNT: 'account',
  USER_ABSENCES: 'absences',
  SETTING: 'settings',
  LOGIN: '/login',
  SSO_CALLBACK: '/auth/callback',
  POLICY: '/policy',
  ANY: '*',
  MAIN: '/',
  MY_TASK: 'my-task',
  DATA_ORGANIZATION: 'organizations/all',
  DEPARTMENT: 'departments',
  STAFF: 'staff',
  STAFF_LIST: 'list',
  STAFF_CREATE: 'create',
  DATA_STAFF_MEMBERS: 'staff-members',
  STAFF_DETAIL: 'staff/:id',
  INTELLIGENCE_CENTER: 'intelligence-center',
  DOCUMENT_LIBRARY: 'document-library',
  DATA_ORGANIZATION_MEMBERS: 'organization-members',
  ORG_MEMBERS: 'org-members',
  DATA_TEAM_MEMBERS: 'team_members',
  TEAMS: 'teams',
  SYSTEMS: 'systems',
  ARTIFACTS: 'artifacts',
  DATA_NOTES: 'notes',
  DATA_FILES: 'files',
  DATA_OVERVIEW: 'overview',
  DATA_PROCESS_CREATE: 'create',
  DATA_ORGANIZATION_CREATE: 'organizations/create',
  CLIENT_CREATE: 'client-setup/create',
  DATA_ORGANIZATION_EDIT: 'organizations/edit',
  DEPARTMENT_CREATE: 'create',
  DEPARTMENT_EDIT: 'edit',
  LICENSE: 'license',
  INIT_CONFIG: 'initial-configuration',
  ADD_ONS: 'add-ons'
};
